import { FC, useState } from 'react';

import toast from 'react-hot-toast';

import { useMutation } from '@apollo/client';
import { IconCheck } from '@tabler/icons-react';

import { Button } from 'ui-kit';

import {
  AcknowledgeSaleDocument,
  AcknowledgeSaleDocumentVariables,
} from 'mutation/__generated__/acknowledgeSaleDocument';
import { ACKNOWLEDGE_SALE_DOCUMENT } from 'mutation/acknowledgeSaleDocument';

import { SaleAcknowledgementRelationship, SaleDocumentType } from '__generated__/globalTypes';

import { COMPANY_SALES_QUERY, OPEN_SALES_QUERY, SALE_ACCESS_QUERY } from 'query/sales';

import { ClickThroughDocument, sha256Hex } from './saleDocuments';
import { PRIMARY_BUTTON_LIGHT_THEME } from './styles';

// api's limit, in UTF-16 code units as String.length counts them. The input has no maxLength:
// it would cut a pasted title silently, and the cut title would go on the CA acceptance.
export const MAX_SIGNER_TITLE_LENGTH = 100;
const REFUSED_TITLE_CHARACTERS = /[\p{Cc}\p{Cf}\p{Cs}]/u;

const SIGNER_RELATIONSHIP_LABELS: Record<SaleAcknowledgementRelationship, string> = {
  [SaleAcknowledgementRelationship.EMPLOYEE]: 'Employee of the bidder',
  [SaleAcknowledgementRelationship.THIRD_PARTY_AFFILIATE]: 'Third-party affiliate of the bidder',
};
const SIGNER_RELATIONSHIPS = Object.keys(
  SIGNER_RELATIONSHIP_LABELS,
) as SaleAcknowledgementRelationship[];

export const signerTitleError = (title: string): string | null => {
  const trimmed = title.trim();
  if (trimmed === '') {
    return 'Enter your title.';
  }
  if (trimmed.length > MAX_SIGNER_TITLE_LENGTH) {
    return `Your title can be at most ${MAX_SIGNER_TITLE_LENGTH} characters.`;
  }
  if (REFUSED_TITLE_CHARACTERS.test(trimmed)) {
    return 'Your title can only contain printable characters.';
  }
  return null;
};

// Blocks everything below it until the caller acknowledges this one document. One document per
// gate, rendered in whatever order the parent's list of outstanding types calls for. SaleAccess
// has no `id` field for Apollo to normalize by, so the parent's saleAccess query needs an
// explicit refetch rather than relying on the mutation's own return value to update the cache.
export const SaleDocumentGate: FC<{
  saleId: string;
  document: ClickThroughDocument;
}> = ({ saleId, document }) => {
  const [agreed, setAgreed] = useState(false);
  // useMutation's own `loading` only turns true once the mutation call actually starts, which is
  // after the await sha256Hex below -- a fast double-click on I Agree fires two hashes and two
  // mutations before `loading` has a chance to disable the button. Harmless server-side (a repeat
  // with the same text keeps the first acknowledgement), but still two calls that should be one.
  const [submitting, setSubmitting] = useState(false);
  const [acknowledge, { loading }] = useMutation<
    AcknowledgeSaleDocument,
    AcknowledgeSaleDocumentVariables
  >(ACKNOWLEDGE_SALE_DOCUMENT, {
    refetchQueries: [
      { query: SALE_ACCESS_QUERY, variables: { saleId } },
      { query: OPEN_SALES_QUERY, variables: {} },
      { query: COMPANY_SALES_QUERY, variables: {} },
    ],
  });
  const { doc_type: docType, title, version, text } = document;
  const titleId = `sale-document-title-${docType}`;
  // The CA is executed by each person, so it records the signer's own title and relationship to
  // the bidder for the sale's access report (SOW §3.8b). The other documents ask for neither.
  const asksSignerDetails = docType === SaleDocumentType.CA;
  const [signerTitle, setSignerTitle] = useState('');
  const [signerTitleTouched, setSignerTitleTouched] = useState(false);
  const signerTitleInputId = `sale-document-signer-title-${docType}`;
  const signerTitleHintId = `${signerTitleInputId}-hint`;
  const signerTitleErrorId = `${signerTitleInputId}-error`;
  const signerTitleProblem = asksSignerDetails ? signerTitleError(signerTitle) : null;
  const showSignerTitleProblem = (signerTitleTouched || agreed) && signerTitleProblem !== null;
  const [signerRelationship, setSignerRelationship] =
    useState<SaleAcknowledgementRelationship | null>(null);
  const signerRelationshipMissing = asksSignerDetails && signerRelationship === null;
  const signerRelationshipErrorId = `sale-document-signer-relationship-${docType}-error`;
  const showSignerRelationshipProblem = agreed && signerRelationshipMissing;
  const continueDisabled =
    !agreed || signerTitleProblem !== null || signerRelationshipMissing || submitting || loading;

  const handleAgree = async () => {
    setSubmitting(true);
    try {
      const documentSha256 = document.document_sha256 ?? (await sha256Hex(text));
      await acknowledge({
        variables: {
          saleId,
          docType,
          version,
          documentSha256,
          ...(asksSignerDetails
            ? { title: signerTitle.trim(), relationship: signerRelationship }
            : {}),
        },
      });
    } catch (mutationError) {
      toast.error(
        mutationError instanceof Error ? mutationError.message : 'Failed to record acknowledgement',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="sale-document-gate"
      data-doc-type={docType}
      className="mt-6 rounded border border-slate-500 border-transparent-0 bg-background-canvas p-4 light:border-gray-200 light:bg-gray-100"
    >
      <h2
        id={titleId}
        className="mb-2 font-heebo text-base font-medium text-white light:text-gray-900"
      >
        {title}
      </h2>
      {/* Focusable so a keyboard user can scroll a document longer than the box. */}
      <pre
        tabIndex={0}
        role="region"
        aria-labelledby={titleId}
        data-testid="sale-document-text"
        className="max-h-96 overflow-auto whitespace-pre-wrap rounded font-sans text-sm text-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 light:text-gray-900 light:focus-visible:ring-pink-700"
      >
        {text}
      </pre>
      {asksSignerDetails && (
        <div className="mt-4">
          <label
            htmlFor={signerTitleInputId}
            className="block text-xs font-bold text-gray-400 light:text-gray-700"
          >
            Your title
          </label>
          <input
            id={signerTitleInputId}
            type="text"
            autoComplete="organization-title"
            required
            data-testid="sale-document-signer-title"
            aria-describedby={
              showSignerTitleProblem
                ? `${signerTitleHintId} ${signerTitleErrorId}`
                : signerTitleHintId
            }
            aria-invalid={showSignerTitleProblem}
            value={signerTitle}
            onChange={(event) => setSignerTitle(event.target.value)}
            onBlur={() => setSignerTitleTouched(true)}
            className="mt-1 w-full max-w-sm rounded border border-gray-600 bg-background-surface px-3 py-1.5 text-sm text-white outline-none focus:border-accent-default light:bg-white light:text-gray-900 light:focus:border-pink-700"
          />
          <p id={signerTitleHintId} className="mt-1 text-xs text-gray-400 light:text-gray-700">
            Your job title, as you sign this agreement. It is recorded with your acceptance and
            shown on the sale&apos;s access report.
          </p>
          {showSignerTitleProblem && (
            <p
              id={signerTitleErrorId}
              role="alert"
              data-testid="sale-document-signer-title-error"
              className="mt-1 text-xs text-status-error light:text-error-muted"
            >
              {signerTitleProblem}
            </p>
          )}
        </div>
      )}
      {asksSignerDetails && (
        <fieldset
          role="radiogroup"
          data-testid="sale-document-signer-relationship"
          aria-describedby={showSignerRelationshipProblem ? signerRelationshipErrorId : undefined}
          aria-invalid={showSignerRelationshipProblem}
          className="mt-4"
        >
          <legend className="block text-xs font-bold text-gray-400 light:text-gray-700">
            Your relationship to the bidder
          </legend>
          {SIGNER_RELATIONSHIPS.map((relationship) => (
            <label
              key={relationship}
              className="mt-2 flex cursor-pointer items-center gap-2 text-sm text-white light:text-gray-900"
            >
              {/* Drawn by hand in light mode, as the agree checkbox is. */}
              <span className="relative inline-flex h-4 w-4">
                <input
                  type="radio"
                  name={`sale-document-signer-relationship-${docType}`}
                  value={relationship}
                  required
                  checked={signerRelationship === relationship}
                  onChange={() => setSignerRelationship(relationship)}
                  className="peer h-4 w-4 light:appearance-none light:rounded-full light:border light:border-gray-600 light:bg-white light:checked:border-pink-700"
                />
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-1 hidden rounded-full bg-pink-700 light:peer-checked:block"
                />
              </span>
              {SIGNER_RELATIONSHIP_LABELS[relationship]}
            </label>
          ))}
          {showSignerRelationshipProblem && (
            <p
              id={signerRelationshipErrorId}
              role="alert"
              data-testid="sale-document-signer-relationship-error"
              className="mt-1 text-xs text-status-error light:text-error-muted"
            >
              Choose your relationship to the bidder.
            </p>
          )}
        </fieldset>
      )}
      <label className="mt-4 flex cursor-pointer items-center gap-2 text-sm text-white light:text-gray-900">
        {/* Drawn by hand in light mode: the native unchecked border varies by browser and
            falls under 3:1 on the gray-100 card in some. */}
        <span className="relative inline-flex h-4 w-4">
          <input
            type="checkbox"
            data-testid="sale-document-agree"
            checked={agreed}
            onChange={(event) => setAgreed(event.target.checked)}
            className="peer h-4 w-4 light:appearance-none light:rounded-sm light:border light:border-gray-600 light:bg-white light:checked:border-pink-700 light:checked:bg-pink-700"
          />
          <IconCheck
            aria-hidden
            stroke={3}
            className="pointer-events-none absolute inset-0 hidden h-4 w-4 text-white light:peer-checked:block"
          />
        </span>
        I have read and agree to the above.
      </label>
      <Button
        className={`mt-3 ${continueDisabled ? '' : PRIMARY_BUTTON_LIGHT_THEME}`}
        size="small"
        type="primary"
        label="I Agree"
        data-testid="sale-document-continue"
        disabled={continueDisabled}
        onClick={handleAgree}
      >
        I Agree
      </Button>
    </div>
  );
};
